<template>
  <section class="modes-section" aria-labelledby="learning-modes-title">
    <div class="modes-inner">
      <h2 id="learning-modes-title" class="modes-title u-display">
        {{ $t("home.modes.title") }}
      </h2>

      <!-- Spotlight 1: personalized courses. The visual is a coded miniature
           of the real course plan (CoursePlan.vue's unit/week/lesson shapes),
           not a screenshot, for the same reason as PhoneChatMockup: raster
           screenshots of the app go stale, HTML/CSS stays under test. The
           whole illustration is aria-hidden because the copy column already
           says everything it shows. -->
      <div class="spot-row">
        <div class="spot-visual">
          <div class="spot-frame spot-frame-course" aria-hidden="true">
            <div class="mini-course">
              <span class="mini-eyebrow">{{ $t("home.modes.miniCourse.unitLabel") }}</span>
              <span class="mini-course-title">{{ $t("home.modes.miniCourse.title") }}</span>
              <span class="mini-course-desc">{{ $t("home.modes.miniCourse.goal") }}</span>
              <div class="mini-week">
                <span class="mini-week-label">{{ $t("home.modes.miniCourse.weekLabel") }}</span>
                <span class="mini-week-title">{{ $t("home.modes.miniCourse.weekTitle") }}</span>
                <div class="mini-lessons">
                  <span class="mini-lesson mini-lesson-done">
                    {{ $t("home.modes.miniCourse.lessonDone") }}
                    <svg class="mini-lesson-mark" viewBox="0 0 24 24" focusable="false">
                      <path d="M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z" />
                    </svg>
                  </span>
                  <span class="mini-lesson mini-lesson-active">
                    {{ $t("home.modes.miniCourse.lessonActive") }}
                  </span>
                  <span class="mini-lesson mini-lesson-locked">
                    {{ $t("home.modes.miniCourse.lessonLocked") }}
                    <svg class="mini-lesson-mark" viewBox="0 0 24 24" focusable="false">
                      <path d="M12 2a5 5 0 0 0-5 5v3H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2h-1V7a5 5 0 0 0-5-5zm-3 8V7a3 3 0 1 1 6 0v3H9z" />
                    </svg>
                  </span>
                </div>
                <div class="mini-progress">
                  <span class="mini-progress-bar"><span class="mini-progress-fill"></span></span>
                  <span class="mini-progress-label">{{ $t("home.modes.miniCourse.progress") }}</span>
                </div>
              </div>
              <div class="mini-week mini-week-collapsed">
                <span>
                  <span class="mini-week-label">{{ $t("home.modes.miniCourse.week2Label") }}</span>
                  <span class="mini-week-title">{{ $t("home.modes.miniCourse.week2Title") }}</span>
                </span>
                <svg class="mini-chevron" viewBox="0 0 24 24" focusable="false">
                  <path d="M7.4 8.6 12 13.2l4.6-4.6L18 10l-6 6-6-6z" />
                </svg>
              </div>
              <div class="mini-adapted">
                <span class="mini-adapted-title">
                  <svg viewBox="0 0 24 24" focusable="false">
                    <path d="M13 3a9 9 0 1 0 8.5 12H19a7 7 0 1 1-1.7-7.3L14 11h8V3l-2.8 2.8A9 9 0 0 0 13 3z" />
                  </svg>
                  {{ $t("home.modes.miniCourse.adaptedTitle") }}
                </span>
                <span class="mini-adapted-body">{{ $t("home.modes.miniCourse.adaptedBody") }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="spot-copy">
          <span class="spot-kicker spot-kicker-course">{{ $t("home.modes.courseTitle") }}</span>
          <h3 class="spot-heading u-display">{{ $t("home.modes.courseHeading") }}</h3>
          <p class="spot-lead">{{ $t("home.modes.courseLead") }}</p>
          <ul class="spot-points" role="list">
            <li v-for="point in coursePoints" :key="point.leadKey" class="spot-point">
              <svg class="spot-point-mark" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z" />
              </svg>
              <span>
                <strong>{{ $t(point.leadKey) }}</strong>
                {{ $t(point.restKey) }}
              </span>
            </li>
          </ul>
        </div>
      </div>

      <!-- Spotlight 2: freestyle chat, mirrored layout. The exchange reuses
           the hero mockup's correction line so the page tells one continuous
           demo story; the Spanish stays Spanish in every locale. -->
      <div class="spot-row spot-row-flip">
        <div class="spot-copy">
          <span class="spot-kicker spot-kicker-chat">{{ $t("home.modes.chatTitle") }}</span>
          <h3 class="spot-heading u-display">{{ $t("home.modes.chatHeading") }}</h3>
          <p class="spot-lead">{{ $t("home.modes.chatLead") }}</p>
          <ul class="spot-points" role="list">
            <li v-for="point in chatPoints" :key="point.leadKey" class="spot-point">
              <svg class="spot-point-mark" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z" />
              </svg>
              <span>
                <strong>{{ $t(point.leadKey) }}</strong>
                {{ $t(point.restKey) }}
              </span>
            </li>
          </ul>
        </div>
        <div class="spot-visual">
          <div class="spot-frame spot-frame-chat" aria-hidden="true">
            <div class="mini-chat">
              <div class="mini-chat-head">
                <img src="@/assets/articulearn-logo-a.png" class="mini-chat-logo" alt="" />
                <span class="mini-chat-titles">
                  <span class="mini-chat-title">{{ $t("home.mockup.title") }}</span>
                  <span class="mini-chat-subtitle">{{ $t("home.mockup.subtitle") }}</span>
                </span>
              </div>
              <div class="mini-topics">
                <span class="mini-topic mini-topic-on">{{ $t("home.modes.miniChat.topic1") }}</span>
                <span class="mini-topic">{{ $t("home.modes.miniChat.topic2") }}</span>
                <span class="mini-topic">{{ $t("home.modes.miniChat.topic3") }}</span>
              </div>
              <div class="mini-turn-tutor">
                <span class="mini-tutor-id">
                  <img src="@/assets/articulearn-logo-a.png" class="mini-tutor-logo" alt="" />
                  <span class="mini-tutor-name">Arti</span>
                </span>
                <p class="mini-tutor-text">{{ $t("home.mockup.artiCorrection") }}</p>
                <span class="mini-audio">
                  <span class="mini-audio-play"></span>
                  <span class="mini-audio-wave"><span v-for="n in 7" :key="n" class="mini-bar"></span></span>
                  <span class="mini-audio-time">0:06</span>
                </span>
              </div>
              <div class="mini-student">
                {{ $t("home.modes.miniChat.studentFollowUp") }}
                <span class="mini-audio mini-audio-student">
                  <span class="mini-audio-play"></span>
                  <span class="mini-audio-wave"><span v-for="n in 5" :key="n" class="mini-bar"></span></span>
                  <span class="mini-audio-time">0:04</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "LearningModesSection",
  data() {
    return {
      // Literal keys rather than keys built in the template: a constructed key
      // is invisible to grep and to any i18n key extractor.
      coursePoints: [
        {
          leadKey: "home.modes.coursePoint1Lead",
          restKey: "home.modes.coursePoint1Rest",
        },
        {
          leadKey: "home.modes.coursePoint2Lead",
          restKey: "home.modes.coursePoint2Rest",
        },
        {
          leadKey: "home.modes.coursePoint3Lead",
          restKey: "home.modes.coursePoint3Rest",
        },
      ],
      chatPoints: [
        {
          leadKey: "home.modes.chatPoint1Lead",
          restKey: "home.modes.chatPoint1Rest",
        },
        {
          leadKey: "home.modes.chatPoint2Lead",
          restKey: "home.modes.chatPoint2Rest",
        },
        {
          leadKey: "home.modes.chatPoint3Lead",
          restKey: "home.modes.chatPoint3Rest",
        },
      ],
    };
  },
};
</script>

<style scoped>
/* Marketing routes are light-mode only (the pre-paint stamp in
   public/index.html gates dark on /dashboard/ and native), so this section has
   no dark-mode branch and stays on the hue tokens the sibling landing sections
   use rather than the role-token layer. */

/* width: 100%, never 100vw: 100vw includes the scrollbar gutter, which is what
   put a horizontal scrollbar on this page in the first place.

   The band is white while HowItWorksSection directly above is --al-bg-light, so
   the two sections keep a visible boundary instead of merging into one long
   tinted run. */
.modes-section {
  background: var(--al-white);
  width: 100%;
}

.modes-inner {
  max-width: 1120px;
  margin: 0 auto;
  padding: 72px 32px;
  display: flex;
  flex-direction: column;
  gap: 64px;
}

/* DESIGN.md type scale, H2: 26px / 1.16 / 700. It is not breakpoint-split, so
   there is no mobile size override below. */
.modes-title {
  font-size: 26px;
  /* public/index.html loads Fraunces at discrete 600/700/800. */
  font-weight: 700;
  line-height: 1.16;
  color: var(--al-ink-900);
  text-align: center;
  margin: 0;
  overflow-wrap: break-word;
}

.spot-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 56px;
  align-items: center;
}

/* Source order puts copy first in the flipped row so the stacked mobile
   layout reads kicker -> heading -> visual without an order override there. */
.spot-copy {
  min-width: 0;
  overflow-wrap: break-word;
}

.spot-kicker {
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  border-radius: var(--al-radius-pill);
  padding: 6px 14px;
  margin-bottom: 14px;
}

.spot-kicker-course {
  background: var(--al-primary-soft);
  color: var(--al-primary-hover);
}

/* No dark-warm text token exists and DESIGN.md forbids new hex values, so the
   kicker text is mixed from the warm accent and ink. */
.spot-kicker-chat {
  background: var(--al-warm-soft);
  color: color-mix(in srgb, var(--al-orange) 55%, var(--al-ink-900));
}

/* DESIGN.md type scale, H1 mobile: 30px / 1.12 / 750 territory; these are the
   emotional Fraunces moments the hero establishes, one per spotlight. */
.spot-heading {
  font-size: 30px;
  font-weight: 700;
  line-height: 1.15;
  color: var(--al-ink-900);
  margin: 0 0 12px;
}

/* DESIGN.md type scale, Body large: 18px / 1.5 / 400. */
.spot-lead {
  font-size: 18px;
  line-height: 1.5;
  color: var(--al-gray);
  margin: 0 0 18px;
}

.spot-points {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.spot-point {
  display: flex;
  gap: 10px;
  font-size: 16px;
  line-height: 1.5;
  color: var(--al-ink-900);
}

.spot-point strong {
  font-weight: 600;
}

.spot-point-mark {
  width: 18px;
  height: 18px;
  fill: var(--al-success);
  flex-shrink: 0;
  margin-top: 3px;
}

.spot-visual {
  min-width: 0;
}

/* Accent-washed frame behind each miniature, mixed from existing tokens
   (DESIGN.md: no new hex values, soft blue/navy elevation only). */
.spot-frame {
  border-radius: var(--al-radius-xl);
  padding: 26px;
  box-shadow: var(--al-shadow-md);
}

.spot-frame-course {
  background: linear-gradient(
    160deg,
    color-mix(in srgb, var(--al-primary-soft) 45%, var(--al-white)),
    var(--al-primary-soft)
  );
}

.spot-frame-chat {
  background: linear-gradient(
    160deg,
    color-mix(in srgb, var(--al-warm-soft) 40%, var(--al-white)),
    var(--al-warm-soft)
  );
}

/* ---- course miniature ---- */

.mini-course {
  position: relative;
  background: var(--al-white);
  border: 1px solid var(--al-gray-300);
  border-radius: var(--al-radius-lg);
  box-shadow: var(--al-shadow-sm);
  padding: 20px 22px;
  display: flex;
  flex-direction: column;
}

.mini-eyebrow {
  font-size: 10px;
  letter-spacing: 1.2px;
  font-weight: 700;
  color: var(--al-primary);
  text-transform: uppercase;
}

.mini-course-title {
  font-size: 17px;
  font-weight: 700;
  color: var(--al-ink-900);
  margin: 2px 0;
}

.mini-course-desc {
  font-size: 12.5px;
  color: var(--al-gray);
  margin-bottom: 14px;
}

.mini-week {
  background: var(--al-bg-light);
  border: 1px solid var(--al-gray-300);
  border-radius: var(--al-radius-sm);
  padding: 11px 13px;
}

.mini-week-label {
  display: block;
  font-size: 9px;
  letter-spacing: 1px;
  font-weight: 700;
  color: var(--al-primary-hover);
  text-transform: uppercase;
}

.mini-week-title {
  display: block;
  font-size: 13.5px;
  font-weight: 600;
  color: var(--al-ink-900);
}

.mini-week-collapsed {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
}

.mini-chevron {
  width: 12px;
  height: 12px;
  fill: var(--al-gray-400);
  flex-shrink: 0;
}

.mini-lessons {
  display: flex;
  gap: 8px;
  margin-top: 9px;
}

.mini-lesson {
  flex: 1;
  border-radius: var(--al-radius-sm);
  padding: 9px 10px;
  font-size: 11.5px;
  font-weight: 600;
  text-align: center;
  position: relative;
  /* A flex child's min-width defaults to min-content, so one long localized
     lesson word (Danish "Drikkepengekultur") would force the row wider than
     the miniature. Let the chips shrink and break the word instead. */
  min-width: 0;
  overflow-wrap: break-word;
}

.mini-lesson-done {
  background: color-mix(in srgb, var(--al-success) 12%, var(--al-white));
  border: 1px solid color-mix(in srgb, var(--al-success) 35%, var(--al-white));
  color: var(--al-success-dark);
}

.mini-lesson-done .mini-lesson-mark {
  fill: var(--al-success-dark);
}

.mini-lesson-active {
  background: var(--al-primary-soft);
  border: 1.5px solid var(--al-primary);
  color: var(--al-primary-hover);
}

.mini-lesson-locked {
  background: var(--al-white);
  border: 1px solid var(--al-gray-300);
  color: var(--al-gray-400);
}

.mini-lesson-locked .mini-lesson-mark {
  fill: var(--al-gray-400);
}

.mini-lesson-mark {
  position: absolute;
  top: 5px;
  right: 6px;
  width: 11px;
  height: 11px;
}

.mini-progress {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 11px;
}

.mini-progress-bar {
  flex: 1;
  height: 6px;
  border-radius: var(--al-radius-pill);
  background: var(--al-gray-200);
  overflow: hidden;
}

.mini-progress-fill {
  display: block;
  height: 100%;
  width: 68%;
  border-radius: var(--al-radius-pill);
  background: var(--al-primary);
}

.mini-progress-label {
  font-size: 11px;
  font-weight: 600;
  color: var(--al-gray);
}

/* The adapt chip overhangs the card edge, the one deliberate "alive" moment:
   it is what "the course reshapes itself" looks like in the product. */
.mini-adapted {
  position: absolute;
  right: -14px;
  bottom: 24px;
  background: var(--al-white);
  border: 1px solid var(--al-gray-300);
  border-radius: var(--al-radius-md);
  box-shadow: var(--al-shadow-md);
  padding: 10px 14px;
  font-size: 12px;
  max-width: 220px;
}

.mini-adapted-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  font-weight: 700;
  color: var(--al-ink-900);
  margin-bottom: 2px;
}

.mini-adapted-title svg {
  width: 14px;
  height: 14px;
  fill: var(--al-orange);
  flex-shrink: 0;
}

.mini-adapted-body {
  color: var(--al-gray);
}

/* ---- chat miniature ---- */

.mini-chat {
  background: var(--al-white);
  border: 1px solid var(--al-gray-300);
  border-radius: var(--al-radius-lg);
  box-shadow: var(--al-shadow-sm);
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.mini-chat-head {
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid var(--al-gray-200);
  padding-bottom: 10px;
}

.mini-chat-logo {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 1px solid var(--al-gray-300);
  background: var(--al-white);
  padding: 2px;
}

.mini-chat-titles {
  display: flex;
  flex-direction: column;
}

.mini-chat-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--al-ink-900);
}

.mini-chat-subtitle {
  font-size: 11px;
  color: var(--al-gray);
}

.mini-topics {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.mini-topic {
  background: var(--al-bg-light);
  border: 1px solid var(--al-gray-300);
  border-radius: var(--al-radius-pill);
  padding: 5px 12px;
  font-size: 11.5px;
  font-weight: 600;
  color: var(--al-gray);
}

.mini-topic-on {
  background: var(--al-primary-soft);
  border-color: var(--al-primary);
  color: var(--al-primary-hover);
}

.mini-tutor-id {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
}

.mini-tutor-logo {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1px solid var(--al-gray-300);
  background: var(--al-white);
  padding: 1px;
}

.mini-tutor-name {
  font-size: 12px;
  font-weight: 700;
  color: var(--al-ink-900);
}

.mini-tutor-text {
  font-size: 13px;
  line-height: 1.5;
  color: var(--al-ink-900);
  margin: 0;
}

.mini-audio {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: var(--al-surface-muted);
  border-radius: var(--al-radius-pill);
  padding: 5px 12px;
  margin-top: 7px;
}

.mini-audio-play {
  width: 0;
  height: 0;
  border-left: 8px solid var(--al-ink-700);
  border-top: 5px solid transparent;
  border-bottom: 5px solid transparent;
}

.mini-audio-wave {
  display: flex;
  align-items: center;
  gap: 2px;
}

.mini-bar {
  display: block;
  width: 2.5px;
  border-radius: 2px;
  background: var(--al-ink-700);
}

.mini-bar:nth-child(1) { height: 6px; }
.mini-bar:nth-child(2) { height: 11px; }
.mini-bar:nth-child(3) { height: 7px; }
.mini-bar:nth-child(4) { height: 12px; }
.mini-bar:nth-child(5) { height: 5px; }
.mini-bar:nth-child(6) { height: 9px; }
.mini-bar:nth-child(7) { height: 6px; }

.mini-audio-time {
  font-size: 10px;
  font-weight: 600;
  color: var(--al-gray);
}

/* The 18/18/6/18 bubble radii are copied verbatim from StudentMessage.vue,
   same as PhoneChatMockup. */
.mini-student {
  align-self: flex-end;
  background: var(--al-warm-soft);
  border-radius: 18px 18px 6px 18px;
  padding: 10px 14px;
  max-width: 85%;
  font-size: 13px;
  line-height: 1.5;
  color: var(--al-ink-900);
}

.mini-student .mini-audio {
  background: color-mix(in srgb, var(--al-white) 70%, transparent);
}

/* Tablet padding matches HeroSection's and HowItWorksSection's. */
@media (max-width: 1023px) {
  .modes-inner {
    padding: 56px 24px;
    gap: 48px;
  }

  .spot-row {
    gap: 32px;
  }

  /* On tablet the columns are ~290px and the row gap 32px, so the chip's
     14px overhang would eat the gap (or clip under an ancestor's
     overflow: hidden). Fold it into the card flow below desktop widths. */
  .mini-adapted {
    position: static;
    margin-top: 10px;
    max-width: none;
  }
}

@media (max-width: 767px) {
  .modes-inner {
    padding: 48px 16px;
    gap: 44px;
  }

  .spot-row {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  /* Copy first on mobile for both rows: the kicker/heading name what the
     miniature below them shows. */
  .spot-row .spot-visual {
    order: 2;
  }

  .spot-row .spot-copy {
    order: 1;
  }

  .spot-heading {
    font-size: 26px;
  }

  .spot-frame {
    padding: 16px;
  }
}
</style>
